import { useLayoutEffect, useRef, useState } from 'react'

type AnyFunction = (...args: any[]) => any

interface UseMutexResult {
  wrap: <T extends AnyFunction>(
    method: T
  ) => (...args: Parameters<T>) => ReturnType<T> | Promise<Awaited<ReturnType<T>>> | undefined
}

export const useMutex = (): UseMutexResult => {
  const [runningState, setRunningState] = useState(false)
  const runningLive = useRef(false)
  useLayoutEffect(() => {
    runningLive.current = runningState
  })

  const cache = useRef<WeakMap<AnyFunction, AnyFunction> | undefined>(undefined)
  if (!cache.current) {
    cache.current = new WeakMap()
  }

  const wrap = <T extends AnyFunction>(
    method: T
  ): ((...args: Parameters<T>) => ReturnType<T> | Promise<Awaited<ReturnType<T>>> | undefined) => {
    if (cache.current!.has(method)) {
      return cache.current!.get(method) as any
    }
    const wrapped = (...props: Parameters<T>) => {
      if (runningLive.current) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('return early because method is running')
        }
        return
      }
      setRunningState(true)

      let result: ReturnType<T>
      try {
        result = method(...props)
      } catch (err) {
        setRunningState(false)
        throw err
      }

      if (typeof (result as PromiseLike<unknown> | undefined)?.then !== 'function') {
        setRunningState(false)
        return result
      } else {
        return Promise.resolve(result).then(
          (r) => {
            setRunningState(false)
            return r as Awaited<ReturnType<T>>
          },
          (err) => {
            setRunningState(false)
            throw err
          }
        )
      }
    }
    cache.current!.set(method, wrapped)
    return wrapped
  }

  return {
    wrap,
  }
}
